import type { CatalogQuery, Course } from '../index.js';

export interface CatalogCourseSeed extends Omit<Course, 'id' | 'upcoming'> {
  upcoming: {
    startDate: string;
    endDate: string;
    capacity: number;
    language: 'fr' | 'en';
  };
}

const catalogMedia: Record<string, { code: string; heroImage: string }> = {
  'litteratie-numerique-cliniciens': {
    code: 'JV-101',
    heroImage:
      'https://images.unsplash.com/photo-1584982751601-97dcc096659c?q=80&w=800&auto=format&fit=crop',
  },
  'emr-agents-cscom': {
    code: 'JV-110',
    heroImage:
      'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?q=80&w=800&auto=format&fit=crop',
  },
  'systemes-information-sante': {
    code: 'JV-120',
    heroImage:
      'https://images.unsplash.com/photo-1587746476666-52f6ffb75bd7?q=80&w=800&auto=format&fit=crop',
  },
  'openmrs-administration': {
    code: 'JV-204',
    heroImage:
      'https://images.unsplash.com/photo-1551601651-2a8555f1a136?q=80&w=800&auto=format&fit=crop',
  },
  'diagnostics-ordinateurs': {
    code: 'JV-201',
    heroImage:
      'https://images.unsplash.com/photo-1517420704952-d9f39e95b43e?q=80&w=800&auto=format&fit=crop',
  },
  'maintenance-terrain-1': {
    code: 'JV-310',
    heroImage:
      'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=800&auto=format&fit=crop',
  },
  'reseaux-fondamentaux': {
    code: 'JV-320',
    heroImage:
      'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=800&auto=format&fit=crop',
  },
  'ia-medicale-fondamentaux': {
    code: 'JV-401',
    heroImage:
      'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=800&auto=format&fit=crop',
  },
  'ia-responsable-sante': {
    code: 'JV-410',
    heroImage:
      'https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?q=80&w=800&auto=format&fit=crop',
  },
  'capteurs-installation': {
    code: 'JV-510',
    heroImage:
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=800&auto=format&fit=crop',
  },
  'infrastructure-telemedecine': {
    code: 'JV-520',
    heroImage:
      'https://images.unsplash.com/photo-1516574187841-cb9cc2ca948b?q=80&w=800&auto=format&fit=crop',
  },
  'raspberry-pi-sante': {
    code: 'JV-610',
    heroImage:
      'https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?q=80&w=800&auto=format&fit=crop',
  },
  'diagnostics-cabinet-dentaire': {
    code: 'JV-710',
    heroImage:
      'https://images.unsplash.com/photo-1606811841689-23dfddceee67?q=80&w=800&auto=format&fit=crop',
  },
};

const catalogBase: CatalogCourseSeed[] = [
  {
    slug: 'litteratie-numerique-cliniciens',
    title: {
      fr: 'Littératie numérique pour clinicien·nes',
      en: 'Core Digital Literacy for Clinicians',
    },
    description: {
      fr: 'Les bases concrètes pour utiliser un ordinateur, un dossier patient et des outils de communication dans un CSCom ou une clinique rurale.',
      en: 'Practical computer, record-keeping, and communication skills for clinicians in CSComs and rural clinics.',
    },
    track: 'digital-health',
    level: 'beginner',
    programmeType: 'workshop',
    durationWeeks: 2,
    deliveryMode: 'in-person',
    certificateType: 'juvo',
    published: true,
    tags: ['CSCom', 'Bamako', 'débutant'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-10-06',
      endDate: '2026-10-17',
      capacity: 18,
      language: 'fr',
    },
  },
  {
    slug: 'emr-agents-cscom',
    title: {
      fr: 'Dossier médical électronique pour agents de CSCom',
      en: 'EMR for Rural CSCom Officers',
    },
    description: {
      fr: 'Mettre en place et utiliser un dossier patient adapté aux CSCom : saisie, recherche, confidentialité et continuité des soins.',
      en: 'Stand up and use a patient record suited to CSComs: capture, search, privacy, and continuity of care.',
    },
    track: 'digital-health',
    level: 'intermediate',
    programmeType: 'certificate',
    durationWeeks: 4,
    deliveryMode: 'hybrid',
    certificateType: 'joint',
    published: true,
    tags: ['EMR', 'CSCom', 'données'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-10-20',
      endDate: '2026-11-14',
      capacity: 16,
      language: 'fr',
    },
  },
  {
    slug: 'systemes-information-sante',
    title: {
      fr: 'Fondamentaux des systèmes d’information de santé',
      en: 'Health Information Systems Fundamentals',
    },
    description: {
      fr: 'Comprendre le parcours des données de santé, des registres papier aux tableaux de bord, sans perdre le lien avec le terrain.',
      en: 'Follow health data from paper registers to dashboards, without losing the link to the clinic floor.',
    },
    track: 'digital-health',
    level: 'beginner',
    programmeType: 'certificate',
    durationWeeks: 3,
    deliveryMode: 'in-person',
    certificateType: 'juvo',
    published: true,
    tags: ['données', 'HIS'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-11-03',
      endDate: '2026-11-21',
      capacity: 20,
      language: 'fr',
    },
  },
  {
    slug: 'openmrs-administration',
    title: {
      fr: 'Administration OpenMRS',
      en: 'OpenMRS Administration',
    },
    description: {
      fr: 'Installer, configurer et maintenir OpenMRS pour un établissement de santé malien : utilisateurs, concepts, sauvegardes.',
      en: 'Install, configure, and maintain OpenMRS for a Malian health facility: users, concepts, backups.',
    },
    track: 'digital-health',
    level: 'advanced',
    programmeType: 'professional',
    durationWeeks: 6,
    deliveryMode: 'hybrid',
    certificateType: 'joint',
    published: true,
    tags: ['OpenMRS', 'EMR'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-11-17',
      endDate: '2026-12-26',
      capacity: 12,
      language: 'fr',
    },
  },
  {
    slug: 'diagnostics-ordinateurs',
    title: {
      fr: 'Diagnostic d’ordinateurs',
      en: 'Computer Diagnostics',
    },
    description: {
      fr: 'Identifier une panne, tester un don, et décider : réparer, récupérer des pièces, ou recycler.',
      en: 'Identify a fault, test a donated machine, and decide: repair, harvest parts, or recycle.',
    },
    track: 'technical',
    level: 'beginner',
    programmeType: 'workshop',
    durationWeeks: 2,
    deliveryMode: 'in-person',
    certificateType: 'juvo',
    published: true,
    tags: ['atelier', 'Circular'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-10-13',
      endDate: '2026-10-24',
      capacity: 14,
      language: 'fr',
    },
  },
  {
    slug: 'maintenance-terrain-1',
    title: {
      fr: 'Maintenance de terrain — niveau 1',
      en: 'Field Maintenance Level 1',
    },
    description: {
      fr: 'Interventions de premier niveau dans un établissement : câblage, alimentation, nettoyage, documentation.',
      en: 'First-line facility work: cabling, power, cleaning, and documentation.',
    },
    track: 'technical',
    level: 'beginner',
    programmeType: 'certificate',
    durationWeeks: 3,
    deliveryMode: 'in-person',
    certificateType: 'juvo',
    published: true,
    tags: ['terrain', 'IT'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-10-27',
      endDate: '2026-11-14',
      capacity: 14,
      language: 'fr',
    },
  },
  {
    slug: 'reseaux-fondamentaux',
    title: {
      fr: 'Fondamentaux des réseaux',
      en: 'Networking Fundamentals',
    },
    description: {
      fr: 'Relier une salle de soins, un bureau et un point d’accès : IP, Wi-Fi, sécurité minimale, diagnostic.',
      en: 'Connect a consulting room, an office, and an access point: IP, Wi-Fi, baseline security, diagnosis.',
    },
    track: 'technical',
    level: 'intermediate',
    programmeType: 'certificate',
    durationWeeks: 4,
    deliveryMode: 'in-person',
    certificateType: 'juvo',
    published: true,
    tags: ['réseau', 'Wi-Fi'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-11-10',
      endDate: '2026-12-05',
      capacity: 12,
      language: 'fr',
    },
  },
  {
    slug: 'ia-medicale-fondamentaux',
    title: {
      fr: 'Fondamentaux de l’IA médicale',
      en: 'Medical AI Fundamentals',
    },
    description: {
      fr: 'Ce que l’IA peut et ne peut pas faire en clinique. Lecture critique, limites, et usages responsables au Mali.',
      en: 'What clinical AI can and cannot do. Critical reading, limits, and responsible use in Mali.',
    },
    track: 'ai',
    level: 'beginner',
    programmeType: 'workshop',
    durationWeeks: 2,
    deliveryMode: 'hybrid',
    certificateType: 'juvo',
    published: true,
    tags: ['IA', 'éthique'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-11-24',
      endDate: '2026-12-05',
      capacity: 22,
      language: 'fr',
    },
  },
  {
    slug: 'ia-responsable-sante',
    title: {
      fr: 'IA responsable en santé',
      en: 'Responsible AI in Healthcare',
    },
    description: {
      fr: 'Biais, consentement, supervision humaine et gouvernance d’un outil d’aide à la décision.',
      en: 'Bias, consent, human oversight, and governance for clinical decision-support tools.',
    },
    track: 'ai',
    level: 'intermediate',
    programmeType: 'certificate',
    durationWeeks: 3,
    deliveryMode: 'hybrid',
    certificateType: 'joint',
    published: true,
    tags: ['IA', 'gouvernance'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-12-07',
      endDate: '2026-12-25',
      capacity: 16,
      language: 'fr',
    },
  },
  {
    slug: 'capteurs-installation',
    title: {
      fr: 'Installation et diagnostic de capteurs',
      en: 'Sensor Installation & Diagnostics',
    },
    description: {
      fr: 'Poser, tester et documenter un capteur biomédical ou environnemental dans un établissement de santé.',
      en: 'Install, test, and document a biomedical or environmental sensor in a health facility.',
    },
    track: 'health-tech',
    level: 'intermediate',
    programmeType: 'certificate',
    durationWeeks: 3,
    deliveryMode: 'in-person',
    certificateType: 'juvo',
    published: true,
    tags: ['capteurs', 'IoT'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-11-03',
      endDate: '2026-11-21',
      capacity: 10,
      language: 'fr',
    },
  },
  {
    slug: 'infrastructure-telemedecine',
    title: {
      fr: 'Infrastructure de télémédecine',
      en: 'Telemedicine Infrastructure',
    },
    description: {
      fr: 'Alimentation, réseau, caméra et salle : ce qu’il faut pour une consultation à distance fiable.',
      en: 'Power, network, camera, and room setup for a reliable remote consultation.',
    },
    track: 'health-tech',
    level: 'intermediate',
    programmeType: 'professional',
    durationWeeks: 5,
    deliveryMode: 'hybrid',
    certificateType: 'joint',
    published: true,
    tags: ['télémédecine', 'infrastructure'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2027-01-12',
      endDate: '2027-02-13',
      capacity: 12,
      language: 'fr',
    },
  },
  {
    slug: 'raspberry-pi-sante',
    title: {
      fr: 'Raspberry Pi pour la santé',
      en: 'Raspberry Pi for Health Facilities',
    },
    description: {
      fr: 'Prototyper un poste sobre, un kiosque ou un collecteur de données avec du matériel accessible.',
      en: 'Prototype a low-power workstation, kiosk, or data collector with accessible hardware.',
    },
    track: 'embedded',
    level: 'beginner',
    programmeType: 'workshop',
    durationWeeks: 2,
    deliveryMode: 'in-person',
    certificateType: 'juvo',
    published: true,
    tags: ['Raspberry Pi', 'fabrication'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2026-12-07',
      endDate: '2026-12-18',
      capacity: 12,
      language: 'fr',
    },
  },
  {
    slug: 'diagnostics-cabinet-dentaire',
    title: {
      fr: 'Diagnostic d’un cabinet dentaire numérique',
      en: 'Dental Suite Diagnostics',
    },
    description: {
      fr: 'Vérifier, relier et maintenir une chaîne dentaire numérique : poste, imageur, impression, dossier.',
      en: 'Check, connect, and maintain a digital dental chain: workstation, imager, printing, records.',
    },
    track: 'digital-dentistry',
    level: 'intermediate',
    programmeType: 'certificate',
    durationWeeks: 4,
    deliveryMode: 'in-person',
    certificateType: 'joint',
    published: true,
    tags: ['dentaire', 'imagerie'],
    location: 'Faso Kanu, Bamako',
    upcoming: {
      startDate: '2027-01-19',
      endDate: '2027-02-13',
      capacity: 10,
      language: 'fr',
    },
  },
];

export const catalogSeed: CatalogCourseSeed[] = catalogBase.map((course) => ({
  ...course,
  ...catalogMedia[course.slug],
}));

export function filterCatalog(courses: CatalogCourseSeed[] | Course[], query: CatalogQuery) {
  const q = query.q?.trim().toLowerCase();
  return courses.filter((course) => {
    if (query.track && course.track !== query.track) return false;
    if (query.level && course.level !== query.level) return false;
    if (query.programmeType && course.programmeType !== query.programmeType) return false;
    if (!q) return true;
    const locale = query.locale ?? 'fr';
    const haystack = [
      course.code ?? '',
      course.slug,
      course.title.fr,
      course.title.en,
      course.description.fr,
      course.description.en,
      course.track,
      ...course.tags,
      course.location,
      course.title[locale],
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

export const impactSeed = {
  period: '2026-Y1',
  kgRefurbished: 0,
  cohortsRun: 0,
  partnersActive: 0,
  jvcIssued: 0,
  jvcRedeemed: 0,
  deployments: 0,
  peopleTrained: 0,
};
